"use client";

import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { IconButton } from './Button';
import { captureFocus, lockDocumentScroll, trapTabKey, unlockDocumentScroll } from './overlayBehavior';

export type SheetProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  /** Which edge the sheet enters from. Bottom is the mobile default. */
  side?: 'bottom' | 'right' | 'left';
  /** Replaces the default padded, scrolling body for panels that own their own scroll. */
  bodyClassName?: string;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  ariaLabel?: string;
};

const SIDE_CLASSES = {
  bottom:
    'inset-x-0 bottom-0 max-h-[85vh] rounded-t-feature border-t pb-mobile-nav-clearance',
  right:
    'inset-y-0 right-0 w-full max-w-md rounded-l-feature border-l',
  left:
    'inset-y-0 left-0 w-full max-w-md rounded-r-feature border-r',
} as const;

/**
 * Edge-anchored panel for filters, detail, and mobile-first flows.
 *
 * Shares its focus trap and reference-counted scroll lock with Modal through
 * overlayBehavior, so the two cannot drift — the reference counting matters when a
 * sheet opens over a dialog, where a naive lock releases the page early.
 *
 * The bottom variant clears the persistent mobile navigation via
 * `pb-mobile-nav-clearance` rather than re-deriving that offset.
 */
export function Sheet({
  isOpen,
  onClose,
  title,
  children,
  side = 'bottom',
  showCloseButton = true,
  closeOnOverlayClick = true,
  initialFocusRef,
  ariaLabel = 'Panel',
  bodyClassName = 'overflow-y-auto p-4',
}: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return;
    lockDocumentScroll();
    const releaseFocus = captureFocus(panelRef.current, initialFocusRef?.current);
    return () => {
      releaseFocus();
      unlockDocumentScroll();
    };
  }, [isOpen, initialFocusRef]);

  if (!isOpen) return null;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    trapTabKey(event, panelRef.current);
  };

  return createPortal(
    <div
      className="ss-overlay-backdrop fixed inset-0 z-overlay flex"
      data-ss-overlay-kind="sheet"
      onMouseDown={(event) => {
        if (closeOnOverlayClick && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        data-ss-overlay-material="transient"
        className={`ss-overlay-panel fixed flex flex-col overflow-hidden shadow-e3 ${SIDE_CLASSES[side]}`}
      >
        {title || showCloseButton ? (
          <div className="ss-overlay-divider flex shrink-0 items-center justify-between gap-4 border-b p-4">
            {title ? (
              <h2 id={titleId} className="text-base font-semibold text-content">
                {title}
              </h2>
            ) : (
              <span />
            )}
            {showCloseButton ? (
              <IconButton label="Close panel" variant="quiet" onClick={onClose}>
                <XMarkIcon className="h-5 w-5" />
              </IconButton>
            ) : null}
          </div>
        ) : null}
        <div className={`min-h-0 flex-1 ${bodyClassName}`} data-ss-overlay-scroll-region>{children}</div>
      </div>
    </div>,
    document.body,
  );
}

export default Sheet;
